import type { Table } from "@tanstack/react-table";
import { Button } from "@/components/ui/base/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/base/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  /**
   * Total row count for display. Pass a number to show "N result(s) total".
   * Pass `undefined` to fall back to client-side filtered count (default for
   * client-side pagination). Pass `null` to hide the count entirely (useful
   * for cursor-based server pagination where the total is unknown).
   */
  totalRows?: number | null;
}

export function DataTablePagination<TData>({
  table,
  totalRows,
}: DataTablePaginationProps<TData>) {
  const displayTotal =
    totalRows === null
      ? null
      : totalRows !== undefined
        ? totalRows
        : table.getFilteredRowModel().rows.length;

  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="text-muted-foreground text-sm">
        {displayTotal !== null
          ? `${displayTotal.toLocaleString()} result(s) total`
          : ""}
      </div>
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-sm">Rows per page</span>
          <Select
            value={String(table.getState().pagination.pageSize)}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 50].map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-muted-foreground text-sm">
          Page {pageIndex + 1}
          {table.getPageCount() > 0 ? ` of ${table.getPageCount()}` : ""}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
